/**
 * Shared Notes Section component for CV detail pages.
 * Used by both CVDetailPage (authenticated) and PublicCVDetailPage (public).
 *
 * UI style from CVNotesSection (CVDetailPage) with props-based interface for flexibility.
 */

import { useEffect } from 'react';
import { Trash2, User } from 'lucide-react';
import { Input } from '@aaas/shared';
import { useTranslation } from 'react-i18next';
import type { CVNote } from '../types';

// ==================== Draft Persistence ====================

type DraftEnvelope = { content: string; savedAt: number };
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ==================== Helper Functions ====================

/**
 * Format date for display in Vietnamese locale.
 */
function formatNoteDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Get display name from a note.
 */
function getNoteDisplayName(note: CVNote, fallback: string): string {
  // Check creator object first
  if (note.creator) {
    if (note.creator.display_name) return note.creator.display_name;
    if (note.creator.email) return note.creator.email;
  }
  // Fall back to creator_name if available
  if (note.creator_name) {
    return note.creator_name;
  }
  return fallback;
}

/**
 * Get first letter for avatar display.
 */
function getAvatarLetter(name: string): string {
  return name.charAt(0).toUpperCase();
}

/**
 * Check if content has HTML tags.
 */
function hasHtmlContent(content: string): boolean {
  return /<[^>]+>/.test(content);
}

/**
 * Parse content into bullet points (split by newlines).
 */
function parseContentToBullets(content: string): string[] {
  return content
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

// ==================== Component Props ====================

export interface NotesSectionProps {
  /** List of notes to display */
  notes: CVNote[];

  /** Loading state for notes list */
  isLoading: boolean;

  /** Submitting state for note creation */
  isSubmitting: boolean;

  /** Current note content in textarea */
  noteContent: string;

  /** Handler for note content change */
  onNoteContentChange: (content: string) => void;

  /** Handler for submitting a new note */
  onSubmitNote: () => void;

  /** Handler for deleting a note (optional - only for authenticated users) */
  onDeleteNote?: (noteId: string) => void;

  /** ID of note currently being deleted */
  deletingNoteId?: string | null;

  /** Creator name input for anonymous users (optional) */
  creatorNameInput?: string;

  /** Handler for creator name change (optional) */
  onCreatorNameChange?: (name: string) => void;

  /** Whether to show the creator name input field */
  showCreatorNameField?: boolean;

  /** Current user's display name (shown when authenticated) */
  currentUserName?: string;

  /** Section title */
  title?: string;

  /** Total note count (for header display) */
  noteCount?: number;

  /** Optional localStorage key for draft persistence; absent = no draft */
  draftKey?: string;
}

// ==================== Component ====================

export function NotesSection({
  notes,
  isLoading,
  isSubmitting,
  noteContent,
  onNoteContentChange,
  onSubmitNote,
  onDeleteNote,
  deletingNoteId,
  creatorNameInput,
  onCreatorNameChange,
  showCreatorNameField = false,
  currentUserName,
  title,
  noteCount,
  draftKey,
}: NotesSectionProps) {
  const { t } = useTranslation('ats');
  // Load draft on mount, with expiry + corrupt-data handling
  useEffect(() => {
    if (!draftKey) return;
    const raw = localStorage.getItem(draftKey);
    if (!raw) return;
    try {
      const draft = JSON.parse(raw) as DraftEnvelope;
      const valid =
        typeof draft?.content === 'string' &&
        typeof draft?.savedAt === 'number' &&
        Date.now() - draft.savedAt < DRAFT_TTL_MS;
      if (valid && !noteContent) {
        onNoteContentChange(draft.content);
      } else if (!valid) {
        localStorage.removeItem(draftKey);
      }
    } catch {
      localStorage.removeItem(draftKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Save draft (debounced 500ms)
  useEffect(() => {
    if (!draftKey) return;
    const timer = setTimeout(() => {
      if (noteContent.trim()) {
        const envelope: DraftEnvelope = { content: noteContent, savedAt: Date.now() };
        localStorage.setItem(draftKey, JSON.stringify(envelope));
      } else {
        localStorage.removeItem(draftKey);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [noteContent, draftKey]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      {/* Header */}
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {title}
        {noteCount !== undefined && ` (${noteCount})`}
      </h3>

      {/* Show current user name if authenticated */}
      {currentUserName && !showCreatorNameField && (
        <p className="text-sm text-gray-500 mb-3 flex items-center gap-2">
          <User className="h-4 w-4" />
          {t('notes.commentingAs')} <strong>{currentUserName}</strong>
        </p>
      )}

      {/* Creator name input for anonymous users (required) */}
      {showCreatorNameField && onCreatorNameChange && (
        <Input
          placeholder={t('notes.namePlaceholder')}
          value={creatorNameInput || ''}
          onChange={(e) => onCreatorNameChange(e.target.value)}
          className="mb-3"
          required
        />
      )}

      {/* Note Input - plain textarea style from CVNotesSection */}
      <textarea
        value={noteContent}
        onChange={(e) => onNoteContentChange(e.target.value)}
        placeholder={t('notes.contentPlaceholder')}
        className="w-full h-32 p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />

      {/* Save Button - right aligned, green style */}
      <div className="flex items-center justify-end mt-4">
        <button
          onClick={onSubmitNote}
          disabled={
            !noteContent.trim() ||
            isSubmitting ||
            (showCreatorNameField && !creatorNameInput?.trim())
          }
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? t('notes.saving') : t('notes.saveNote')}
        </button>
      </div>

      {/* Notes List */}
      {isLoading ? (
        <div className="mt-6 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
        </div>
      ) : notes.length > 0 ? (
        <div className="mt-6 space-y-4">
          {notes.map((note) => {
            const displayName = getNoteDisplayName(note, t('notes.anonymous'));
            const avatarLetter = getAvatarLetter(displayName);

            return (
              <NoteItem
                key={note.id}
                note={note}
                displayName={displayName}
                avatarLetter={avatarLetter}
                onDelete={onDeleteNote}
                isDeleting={deletingNoteId === note.id}
                t={t}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ==================== Note Item Component ====================

interface NoteItemProps {
  note: CVNote;
  displayName: string;
  avatarLetter: string;
  onDelete?: (noteId: string) => void;
  isDeleting?: boolean;
  t: (key: string) => string;
}

function NoteItem({ note, displayName, avatarLetter, onDelete, isDeleting, t }: NoteItemProps) {
  const bullets = parseContentToBullets(note.content);
  const isHtml = hasHtmlContent(note.content);

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="flex justify-between items-start">
        <div className="flex gap-3">
          {/* Avatar */}
          <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white font-medium shrink-0">
            {avatarLetter}
          </div>

          {/* Name and date */}
          <div>
            <span className="font-medium text-gray-900">{displayName}</span>
            <p className="text-xs text-gray-500">{formatNoteDate(note.created_at)}</p>
          </div>
        </div>

        {/* Delete button - only show if onDelete provided */}
        {onDelete && (
          <button
            onClick={() => onDelete(note.id)}
            disabled={isDeleting}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
            title={t('notes.deleteNote')}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Note content */}
      {isHtml ? (
        <div
          className="mt-2 ml-13 text-sm text-gray-700 break-words [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-gray-700"
          dangerouslySetInnerHTML={{ __html: note.content }}
        />
      ) : bullets.length > 1 ? (
        <ul className="mt-2 space-y-1 ml-13">
          {bullets.map((bullet, idx) => (
            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">•</span>
              <span className="break-words min-w-0">{bullet}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 ml-13 text-sm text-gray-700 whitespace-pre-wrap break-words">
          {note.content}
        </p>
      )}
    </div>
  );
}
